/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */

/**
 * The saga for page ExpensesAnalyzer
 *
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.16.0
 * @since 1.1.0
 */
import { createElement } from 'react';

import orderBy from 'lodash/orderBy';
import moment from 'moment';
import { SagaIterator } from 'redux-saga';
import { all, call, put, select, take, takeEvery } from 'redux-saga/effects';

import { EChartDataKeyEnum, IChartDataInput } from '../../components/common/Chart/chartTypes';
import { SortEnum } from '../../components/Table/components/Filters/Filter';
import { TABLE_LOAD_SIZE } from '../../components/Table/constants';
import {
    DROP_TABLE_FILTER_AND_SORT,
    LOAD_TABLE_FIELDS_SUCCESSFUL,
    LOAD_TABLE_LIST_ERROR,
    LOAD_TABLE_LIST_SUCCESSFUL,
    SET_RANGE_MONTH_FILTER,
} from '../../components/Table/constants/actionConstants';
import {
    makeSelectorBasketContext,
    makeSelectorTableBasketCount,
    makeSelectorTableCurrentName,
    makeSelectorTableData,
    makeSelectorTableFields,
    makeSelectorTableFilter,
} from '../../components/Table/selectors/tableSelectors';
import {
    TableNameType,
    TLoadTableFieldsSuccessfulAction,
    TSetRangeMonthFilterAction,
} from '../../components/Table/types/tableTypes';
import { API_HOSTNAME, FILTER_AND_SORT } from '../../modules/app/Api';
import { appHistoryPush } from '../../modules/app/appHistory';
import { makeSelectTranslationCommon } from '../../modules/internationalization/intSelectors';
import { ICommonTranslation } from '../../modules/internationalization/types';
import ModalErrorCommon from '../../modules/modal/components/ModalError';
import { modalOpen } from '../../modules/modal/modalActions';
import { IModalRenderProps } from '../../modules/modal/types/modalTypes';
import {
    onboardingInitStartAction,
    onboardingPageReadyForStartAction,
} from '../../modules/onboarding/onboardingActions';
import { ONBOARDING_INIT_SUCCESS } from '../../modules/onboarding/onboardingConstants';
import { EOnboardingPage } from '../../modules/onboarding/types/onboardingTypes';
import { makeSelectWizardCallsDetailEnabled } from '../../modules/role/selectors/guardSelector';
import { ExternalRoutePath, openExternalRoute } from '../../routes/constants/externalRoutes';
import { CLIENT_ID, RoutePath } from '../../routes/constants/routesConstants';
import { filterToJson } from '../../sagas/filterToJson';
import { requestSaga } from '../../sagas/requestSaga';
import { formQueryParamsString } from '../../sagas/utils/formQueryParamsString';
import { stringFormat } from '../../utils/stringFormat';
import { EFormatData } from '../PageDashboard/types/widgetTypes';
import { EExpensesAnalyzerChart } from './components/ExpensesAnalyzerController/expensesAnalyzerControllerTypes';
import { MapExpensesAnalyzerTypeToApiPath } from './components/ExpensesAnalyzerTable/types/expensesAnalyzerTableMapTypes';
import {
    EExpensesAnalyzerField,
    IExpensesAnalyzerData,
} from './components/ExpensesAnalyzerTable/types/expensesAnalyzerTableTypes';
import {
    fulledExpensesAccountsListAction,
    getExpensesAccountsListFailed,
    getExpensesAccountsListSuccess,
    loadChartsExpensesAnalyzerAction,
    loadChartsExpensesAnalyzerErrorAction,
    loadChartsExpensesAnalyzerSuccessfulAction,
    setExpensesAnalyzerLoadingChartAction,
    updateChartItemExpensesAnalyzerSuccessfulAction,
} from './pageExpensesAnalyzerActions';
import {
    EXPENSES_ANALYZER_GET_ACCOUNTS_LIST,
    EXPENSES_ANALYZER_INITIALIZE_CHART,
    EXPENSES_ANALYZER_LOAD_CHART,
    EXPENSES_ANALYZER_REDIRECT_CALLS_DETAIL,
    EXPENSES_ANALYZER_REDIRECT_DAILY_EXPENSES,
    EXPENSES_ANALYZER_REDIRECT_SUBSCRIBERS_EXPENSES,
    EXPENSES_ANALYZER_UPDATE_CHART_ITEM,
    EXPENSES_ANALYZER_LOAD_CHART_SUCCESSFUL,
    EXPENSES_ANALYZER_LOAD_CHART_ERROR,
} from './pageExpensesAnalyzerConstants';
import {
    makeSelectorExpensesAccountsApiPathConfiguration,
    makeSelectorExpensesAccountsList,
    makeSelectorExpensesAnalyzerCurrentAccount,
    makeSelectorExpensesAnalyzerCurrentChartData,
    makeSelectorExpensesAnalyzerSelectedId,
    makeSelectorExpensesAnalyzerType,
} from './pageExpensesAnalyzerSelectors';
import {
    TInitializeChartExpensesAnalyzerAction,
    TUpdateChartExpensesAnalyzerAction,
} from './types/pageExpensesAnalyzerActionTypes';
import {
    mapChartTypeToMinMonthCount,
    MapExpensesAnalyzerTypeToMeta,
    mapExpensesBillsTypeToSubscribersApi,
    mapExpensesTypeToCallsDetailRoutePath,
    mapExpensesTypeToOnboarding,
} from './types/pageExpensesAnalyzerMapTypes';
import { EExpensesAnalyzerTypes, TChartIdField } from './types/pageExpensesAnalyzerTypes';
import { correctRangeByMinMonthCount } from './utils/correctRangeByMinMonthCount';
import { mapTableDataToChartCategoryData } from './utils/mapTableDataToChartCategoryData';
import { mapTableDataToChartDynamicData } from './utils/mapTableDataToChartDynamicData';
import { mapTableDataToChartLabels } from './utils/mapTableDataToChartLabels';

export function* loadChartExpensesAnalyzerSaga(): SagaIterator {

    let data, dynamicData;
    try {
        const apiPathChart: string = (yield select(makeSelectorExpensesAccountsApiPathConfiguration())).chart;
        const selectedFilters = yield select(makeSelectorTableFilter());
        const expensesAnalyzerType: EExpensesAnalyzerTypes = yield select(makeSelectorExpensesAnalyzerType());
        const selectedId: number = yield select(makeSelectorExpensesAnalyzerSelectedId());
        const { isDynamicDisabled } = MapExpensesAnalyzerTypeToMeta[expensesAnalyzerType];
        const chartIdField: TChartIdField = selectedId ?
            EExpensesAnalyzerField.MSISDN :
            MapExpensesAnalyzerTypeToMeta[expensesAnalyzerType].chartIdField;

        const from = selectedFilters?.monthRange?.valueFrom ?
            moment(selectedFilters?.monthRange?.valueFrom) :
            moment();
        const to = selectedFilters?.monthRange?.valueTo ?
            moment(selectedFilters?.monthRange?.valueTo) :
            moment();
        const url = stringFormat(apiPathChart,
            from.clone().format(EFormatData.YearMonthReverse),
            to.clone().format(EFormatData.YearMonthReverse));

        const rangeDiff = to.diff(from, 'months') + 1;
        const minMonthCount = mapChartTypeToMinMonthCount[EExpensesAnalyzerChart.DYNAMIC];
        if (!isDynamicDisabled && rangeDiff < minMonthCount) {
            const [fromDynamic, toDynamic] =
                correctRangeByMinMonthCount({
                    rangeStart: from,
                    rangeEnd: to,
                    minMonthCount,
                }).map(date => date.format(EFormatData.YearMonthReverse));
            const urlDynamic = stringFormat(apiPathChart, fromDynamic, toDynamic);
            [data, dynamicData] = yield all([
                call(requestSaga, url, 'GET'),
                call(requestSaga, urlDynamic, 'GET'),
            ]);
            data = data['items'];
            dynamicData = dynamicData['items'];
        } else {
            data = (yield call(requestSaga, url, 'GET'))['items'];
        }

        const sortedData = orderBy(data, EExpensesAnalyzerField.AMOUNT_TOTAL, 'desc');

        const fields = yield select(makeSelectorTableFields(TableNameType.expensesAnalyzer));

        const chartDataCategory: IChartDataInput[] =
            mapTableDataToChartCategoryData(sortedData, chartIdField, fields);
        const chartLabels = mapTableDataToChartLabels(data, chartIdField);

        yield put(loadChartsExpensesAnalyzerSuccessfulAction({
            type: EExpensesAnalyzerChart.CATEGORY,
            data: chartDataCategory,
            labels: chartLabels,
            chartCategoryRange: {
                from: from.format(EFormatData.YearMonthReverse),
                to: to.format(EFormatData.YearMonthReverse),
            },
        }));

        if (!isDynamicDisabled) {
            const translation: ICommonTranslation = yield select(makeSelectTranslationCommon());
            const sortedDynamicData = orderBy(dynamicData || data, chartIdField, 'asc');
            const chartDataDynamic: IChartDataInput[] =
                mapTableDataToChartDynamicData(sortedDynamicData, translation.calendar, chartIdField);

            yield put(loadChartsExpensesAnalyzerSuccessfulAction({
                type: EExpensesAnalyzerChart.DYNAMIC,
                data: chartDataDynamic,
                labels: chartLabels,
            }));
        }
    } catch (e: any) {
        yield put(loadChartsExpensesAnalyzerErrorAction(e));
    }
}

export function* updateChartExpensesSaga(action: TUpdateChartExpensesAnalyzerAction): SagaIterator {
    const chartData: IChartDataInput[] = yield select(makeSelectorExpensesAnalyzerCurrentChartData());
    const updateData = chartData.map((item: IChartDataInput) => {
        const newItem: IChartDataInput = { ...item };
        if (newItem[EChartDataKeyEnum.order] === action.payload[EChartDataKeyEnum.order]) {
            newItem[EChartDataKeyEnum.checked] = !newItem[EChartDataKeyEnum.checked];
        }
        return newItem;
    });

    yield put(updateChartItemExpensesAnalyzerSuccessfulAction(updateData));
}

type TInitChartExpensesAnalyzerSagaActions = TInitializeChartExpensesAnalyzerAction
    | TLoadTableFieldsSuccessfulAction
    | TSetRangeMonthFilterAction;

export function* initChartExpensesAnalyzerSaga(action: TInitChartExpensesAnalyzerSagaActions): SagaIterator {
    const tableNameType: TableNameType = yield select(makeSelectorTableCurrentName());
    const context: string | undefined = yield select(makeSelectorBasketContext());
    if (action.type === LOAD_TABLE_FIELDS_SUCCESSFUL &&
        tableNameType !== TableNameType.expensesAnalyzer) return;

    // если таблица вызывается с контекстом (НЕ из своего основного места),
    // то запрос расходов не требуется
    if (!!context) {
        return;
    }

    // wait for table data
    yield put(setExpensesAnalyzerLoadingChartAction(true));
    const tableListAction = yield take([LOAD_TABLE_LIST_SUCCESSFUL, LOAD_TABLE_LIST_ERROR]);
    if (tableListAction.type === LOAD_TABLE_LIST_ERROR) {
        yield put(loadChartsExpensesAnalyzerErrorAction(tableListAction.payload.error));
        return;
    }
    const basketCount: number = yield select(makeSelectorTableBasketCount());
    if (basketCount) {
        yield put(loadChartsExpensesAnalyzerAction());
        return;
    }

    // Only for expensesAnalyzer
    const currentTableName = yield select(makeSelectorTableCurrentName());
    if (currentTableName && currentTableName !== TableNameType.expensesAnalyzer) {
        return;
    }

    try {
        const apiPathDetails: string = (yield select(makeSelectorExpensesAccountsApiPathConfiguration())).details;
        const tableData: IExpensesAnalyzerData[] = yield select(makeSelectorTableData(TableNameType.expensesAnalyzer));
        const expensesAnalyzerType: EExpensesAnalyzerTypes = yield select(makeSelectorExpensesAnalyzerType());
        const entityId = yield select(makeSelectorExpensesAnalyzerCurrentAccount());
        const selectedId: number = yield  select(makeSelectorExpensesAnalyzerSelectedId());
        const selectedFilters = yield select(makeSelectorTableFilter());
        const { isDynamicDisabled } = MapExpensesAnalyzerTypeToMeta[expensesAnalyzerType];
        const chartIdField: TChartIdField = selectedId ?
            EExpensesAnalyzerField.MSISDN :
            MapExpensesAnalyzerTypeToMeta[expensesAnalyzerType].chartIdField;

        const sortedData = orderBy(tableData, EExpensesAnalyzerField.AMOUNT_TOTAL, 'desc').slice(0, 10);
        const fields = yield select(makeSelectorTableFields());
        const chartDataCategory: IChartDataInput[] = mapTableDataToChartCategoryData(sortedData, chartIdField, fields);
        const chartLabels = mapTableDataToChartLabels(sortedData, chartIdField);
        const fromCategory = selectedFilters?.monthRange?.valueFrom ? selectedFilters.monthRange.valueFrom :
            moment().format(EFormatData.YearMonthReverse);
        const toCategory = selectedFilters?.monthRange?.valueTo ? selectedFilters?.monthRange?.valueTo :
            moment().format(EFormatData.YearMonthReverse);

        yield put(loadChartsExpensesAnalyzerSuccessfulAction({
            type: EExpensesAnalyzerChart.CATEGORY,
            data: chartDataCategory,
            labels: chartLabels,
            chartCategoryRange: {
                from: fromCategory,
                to: toCategory,
            },
        }));

        const translation: ICommonTranslation = yield select(makeSelectTranslationCommon());

        let from = selectedFilters?.monthRange?.valueFrom ? moment(selectedFilters.monthRange.valueFrom) :
            moment().subtract(2, 'month');
        let to = selectedFilters?.monthRange?.valueTo ? moment(selectedFilters?.monthRange?.valueTo) : moment();
        [from, to] = correctRangeByMinMonthCount({
            rangeStart: from,
            rangeEnd: to,
            minMonthCount: mapChartTypeToMinMonthCount[EExpensesAnalyzerChart.DYNAMIC],
        });

        if (!isDynamicDisabled) {
            let API =
                stringFormat(apiPathDetails, from.format(EFormatData.YearMonthReverse),
                    to.format(EFormatData.YearMonthReverse));

            if (entityId && !isNaN(entityId)) {
                API += `&id=${entityId}`;
            }

            const dynamicData = yield call(requestSaga, API, 'GET');
            const sortedDynamicData = orderBy(dynamicData.elements, chartIdField, 'asc')
                .slice(0, 10);
            const chartDataDynamic: IChartDataInput[] =
                mapTableDataToChartDynamicData(sortedDynamicData, translation.calendar, chartIdField);

            yield put(loadChartsExpensesAnalyzerSuccessfulAction({
                type: EExpensesAnalyzerChart.DYNAMIC,
                data: chartDataDynamic,
                labels: chartLabels,
            }));
        }
    } catch (e: any) {
        yield put(loadChartsExpensesAnalyzerErrorAction(e));
    }

}

function* expensesAnalyzerRedirectSubscribersExpensesSaga(): SagaIterator {
    const currentAccount = yield select(makeSelectorExpensesAnalyzerCurrentAccount());
    const expensesAnalyzerType: EExpensesAnalyzerTypes = yield select(makeSelectorExpensesAnalyzerType());
    const { isBillsType } = MapExpensesAnalyzerTypeToMeta[expensesAnalyzerType];
    try {
        if (!isBillsType) {
            const url = MapExpensesAnalyzerTypeToApiPath[EExpensesAnalyzerTypes.AccountSingleSubscribers].common.api;
            const currentFilter = yield select(makeSelectorTableFilter(TableNameType.expensesAnalyzer));
            const filter = currentFilter?.monthRange ? { monthRange: currentFilter.monthRange } : {};
            if (Object.keys(filter).length) {
                const filterAndSort = {
                    filter,
                    sort: {
                        property: EExpensesAnalyzerField.AMOUNT_TOTAL,
                        direction: SortEnum.descending,
                    },
                };
                const json = filterToJson(filterAndSort);
                yield call(requestSaga, API_HOSTNAME + url + FILTER_AND_SORT, 'POST', json);
            }
            appHistoryPush(currentAccount ?
                RoutePath.expensesAnalyzerAccountIdSubscribers.replace(CLIENT_ID, currentAccount) :
                RoutePath.expensesAnalyzerAccountSubscribers);
        } else {
            const url = mapExpensesBillsTypeToSubscribersApi[expensesAnalyzerType]
                .replace(CLIENT_ID, currentAccount ?? '');
            appHistoryPush(url);
        }

    } catch (e) {
        const translation: ICommonTranslation = yield select(makeSelectTranslationCommon());
        yield put(modalOpen({
            render: (props: IModalRenderProps) => createElement(ModalErrorCommon, {
                onCloseModal: props.onCloseModal,
                translation: translation.modal,
            }),
        }));
    }
}

function* expensesAnalyzerRedirectDailyExpensesSaga(): SagaIterator {
    const expensesAnalyzerType: EExpensesAnalyzerTypes = yield select(makeSelectorExpensesAnalyzerType());
    const currentAccount: number = yield select(makeSelectorExpensesAnalyzerCurrentAccount());
    const selectedAccount: number = yield select(makeSelectorExpensesAnalyzerSelectedId());
    const queryParams = {
        expensesType: expensesAnalyzerType, currentAccount: selectedAccount ?
            selectedAccount.toString() : currentAccount ? currentAccount.toString() : '',
    };
    let url: string;

    if ([
        EExpensesAnalyzerTypes.AccountSingleSubscribers,
        EExpensesAnalyzerTypes.AccountSubscribers,
        EExpensesAnalyzerTypes.BillsAccountsSubscribers,
        EExpensesAnalyzerTypes.BillsAccountSingleSubscribers,
        EExpensesAnalyzerTypes.BillsUnpaidAccountSingleSubscribers,
        EExpensesAnalyzerTypes.BillsUnpaidAccountsSubscribers,
        EExpensesAnalyzerTypes.SubscriberMobile,
        EExpensesAnalyzerTypes.SubscriberMobileSingle,
    ].includes(expensesAnalyzerType) || selectedAccount) {
        url = ExternalRoutePath.expensesAnalyzerSubscriberMobileBasket;
    } else if ([
        EExpensesAnalyzerTypes.SubscriberFixed,
        EExpensesAnalyzerTypes.SubscriberFixedSingle,
    ].includes(expensesAnalyzerType)) {
        url = ExternalRoutePath.expensesAnalyzerSubscriberMobileBasket;
    } else {
        url = currentAccount ? stringFormat(ExternalRoutePath.expensesAnalyzerAccountId, currentAccount) :
            ExternalRoutePath.expensesAnalyzerAccountBasket;
    }

    openExternalRoute(url + formQueryParamsString(queryParams));
}

function* expensesAnalyzerRedirectCallsDetailSaga(): SagaIterator {
    const callsDetailEnabled: boolean = yield select(makeSelectWizardCallsDetailEnabled());
    if (!callsDetailEnabled) {
        openExternalRoute(ExternalRoutePath.mobileExpensesDetails);
        return;
    }

    const expensesAnalyzerType: EExpensesAnalyzerTypes = yield select(makeSelectorExpensesAnalyzerType());
    const currentAccount: number = yield select(makeSelectorExpensesAnalyzerCurrentAccount());
    const url = mapExpensesTypeToCallsDetailRoutePath[expensesAnalyzerType];
    if (url) appHistoryPush(url.replace(CLIENT_ID, currentAccount.toString()));
}

function* getAccountsListExpensesAnalyzerSaga(): SagaIterator {
    try {
        const expensesAnalyzerType = yield select(makeSelectorExpensesAnalyzerType());
        const enityId = yield select(makeSelectorExpensesAnalyzerCurrentAccount());
        const url = MapExpensesAnalyzerTypeToApiPath[expensesAnalyzerType].all?.accountsList;
        const data = yield select(makeSelectorExpensesAccountsList());

        const queryParams = [`from=${data.length}`, `size=${TABLE_LOAD_SIZE}`];
        if (enityId) {
            queryParams.push(`id=${enityId}`);
        }
        const queryString = queryParams.join('&');
        const newData = yield call(requestSaga, API_HOSTNAME + url + '?' + queryString, 'GET');

        if (newData.length) {
            yield put(getExpensesAccountsListSuccess([].concat(data, newData)));
        } else {
            yield put(fulledExpensesAccountsListAction());
        }
    } catch (error: any) {
        yield put(getExpensesAccountsListFailed(error));
    }
}

export function* getTableAccountIdExpensesSaga(): SagaIterator {
    const entityId = yield select(makeSelectorExpensesAnalyzerCurrentAccount());
    const selectedId = yield select(makeSelectorExpensesAnalyzerSelectedId());
    const expensesAnalyzerType: EExpensesAnalyzerTypes = yield select(makeSelectorExpensesAnalyzerType());
    const { isBillsType } = MapExpensesAnalyzerTypeToMeta[expensesAnalyzerType];

    return selectedId || (!isBillsType && entityId) || 0;
}

export function* initOnboardingExpensesAnalyzer(): SagaIterator {
    const expensesAnalyzerType: EExpensesAnalyzerTypes = yield select(makeSelectorExpensesAnalyzerType());
    if (!!mapExpensesTypeToOnboarding[expensesAnalyzerType]) {
        yield take([EXPENSES_ANALYZER_LOAD_CHART_SUCCESSFUL, EXPENSES_ANALYZER_LOAD_CHART_ERROR]);

        yield put(onboardingInitStartAction({ page: EOnboardingPage.expensesAnalyzerAccount }));
        yield take(ONBOARDING_INIT_SUCCESS);

        yield put(onboardingPageReadyForStartAction());
    }
}

export function* expensesAnalyzerSagas(): SagaIterator {
    yield all([
        takeEvery(EXPENSES_ANALYZER_INITIALIZE_CHART, initOnboardingExpensesAnalyzer),
        takeEvery([
            DROP_TABLE_FILTER_AND_SORT,
            EXPENSES_ANALYZER_INITIALIZE_CHART,
            LOAD_TABLE_FIELDS_SUCCESSFUL,
            SET_RANGE_MONTH_FILTER,
        ], initChartExpensesAnalyzerSaga),
        takeEvery(EXPENSES_ANALYZER_LOAD_CHART, loadChartExpensesAnalyzerSaga),
        takeEvery(EXPENSES_ANALYZER_GET_ACCOUNTS_LIST, getAccountsListExpensesAnalyzerSaga),
        takeEvery(EXPENSES_ANALYZER_UPDATE_CHART_ITEM, updateChartExpensesSaga),
        takeEvery(EXPENSES_ANALYZER_REDIRECT_CALLS_DETAIL, expensesAnalyzerRedirectCallsDetailSaga),
        takeEvery(EXPENSES_ANALYZER_REDIRECT_SUBSCRIBERS_EXPENSES, expensesAnalyzerRedirectSubscribersExpensesSaga),
        takeEvery(EXPENSES_ANALYZER_REDIRECT_DAILY_EXPENSES, expensesAnalyzerRedirectDailyExpensesSaga),
    ]);
}
